import * as React from 'react';
import {
  DashboardDataTableColumnKind,
  DashboardTableMultiSelectFilter,
  DashboardTableSearchInput,
  DashboardTableDialogBody,
  DashboardTableDialogContent,
  DashboardTableDialogDataTable,
  DashboardTableDialogFooter,
  DashboardTableDialogHeader,
  buildDashboardDataTableColumns,
  useDashboardDataTableState,
} from '@shigo-ui/dashboard-ui';
import { Button, Dialog } from '@shigo-ui/ui';

import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../lib/messages';
import { usePlacementQuery } from '../../../../supply/placements/hooks';
import type { Placement } from '../../../../supply/placements/model';
import { useIntegrationsByIdsQuery } from '../../hooks';
import {
  IntegrationStatus,
  integrationPageSizes,
  integrationStatusLabel,
  integrationStatusTone,
  type Integration,
} from '../../model';

export function PlacementIntegrationsDialog({
  placement,
  onOpenChange,
}: {
  placement: Placement;
  onOpenChange: (open: boolean) => void;
}) {
  const [filter, setFilter] = React.useState({
    searchTerm: '',
    statuses: [] as IntegrationStatus[],
  });
  const placementQuery = usePlacementQuery(placement.id);
  const associationIds = React.useMemo(() => {
    const ids = (
      placementQuery.data?.placementDemandIntegrations ?? placement.placementDemandIntegrations
    ).flatMap((integration) => {
      const id = integration.demandIntegrationId ?? integration.id;
      return id ? [id] : [];
    });
    return [...new Set(ids)];
  }, [placement.placementDemandIntegrations, placementQuery.data?.placementDemandIntegrations]);
  const integrationsQuery = useIntegrationsByIdsQuery(associationIds);
  const normalizedSearch = filter.searchTerm.trim().toLocaleLowerCase();
  const integrationsById = new Map(integrationsQuery.data?.content.map((item) => [item.id, item]));
  const integrations = associationIds.flatMap((id) => {
    const integration = integrationsById.get(id);
    return integration ? [integration] : [];
  });
  const matchingIntegrations = integrations.filter((integration) => {
    const status = integration.status;
    if (!normalizedSearch) return true;
    return [integration.name, integration.demandPartner.displayName, integrationStatusLabel(status)]
      .filter(Boolean)
      .some((value) => String(value).toLocaleLowerCase().includes(normalizedSearch));
  });
  // The association lookup loads every matching ID, before local pagination/status filtering.
  const selectedStatuses = new Set(filter.statuses);
  const filtered = matchingIntegrations.filter(
    (integration) => !selectedStatuses.size || selectedStatuses.has(integration.status),
  );
  const table = useDashboardDataTableState({
    initialPageSize: 5,
    totalItems: filtered.length,
    pageSizes: integrationPageSizes,
  });
  const search = useDebouncedValue(
    filter.searchTerm,
    (searchTerm) => {
      setFilter((current) => ({ ...current, searchTerm }));
      table.resetPage();
    },
    650,
  );
  const visible = filtered.slice(table.page * table.pageSize, (table.page + 1) * table.pageSize);
  const columns = buildDashboardDataTableColumns<Integration>([
    {
      id: 'name',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('integrations.name'),
      headerClassName: 'w-72 pl-4',
      className: 'w-72 pl-4 font-medium',
      value: (integration) => integration.name,
    },
    {
      id: 'status',
      kind: DashboardDataTableColumnKind.Status,
      header: dashboardT('integrations.status'),
      headerClassName: 'w-32',
      className: 'w-32',
      label: (integration) => {
        const status = integration.status;
        return integrationStatusLabel(status);
      },
      tone: (integration) => {
        const status = integration.status;
        return integrationStatusTone(status);
      },
    },
    {
      id: 'demandPartner',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('integrations.demandPartner'),
      headerClassName: 'w-60',
      className: 'w-60',
      value: (integration) => integration.demandPartner.displayName ?? '-',
    },
  ]);
  const statusLabel = filter.statuses.length
    ? filter.statuses.map(integrationStatusLabel).join(', ')
    : dashboardT('integrations.allStatuses');

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DashboardTableDialogContent>
        <DashboardTableDialogHeader
          title={dashboardT('placements.integrationsFor', { name: placement.name })}
        />
        <DashboardTableDialogBody>
          <DashboardTableDialogDataTable
            tableLabel={dashboardT('placements.integrationsFor', { name: placement.name })}
            columns={columns}
            items={visible}
            loading={placementQuery.isLoading || integrationsQuery.isLoading}
            errorMessage={
              placementQuery.isError || integrationsQuery.isError
                ? dashboardT('integrations.loadError')
                : undefined
            }
            getRowKey={(integration) => integration.id}
            emptyMessage={dashboardT('integrations.empty')}
            minWidthClassName="min-w-[760px]"
            toolbar={
              <>
                <DashboardTableSearchInput
                  aria-label={dashboardT('integrations.search')}
                  placeholder={dashboardT('integrations.search')}
                  value={search.draft}
                  onChange={(event) => {
                    search.setDraft(event.target.value);
                  }}
                />
                <DashboardTableMultiSelectFilter
                  ariaLabel={dashboardT('integrations.statusFilter')}
                  label={statusLabel}
                  value={filter.statuses}
                  options={Object.values(IntegrationStatus).map((status) => ({
                    value: status,
                    label: integrationStatusLabel(status),
                    count: integrationsQuery.data
                      ? matchingIntegrations.filter((integration) => integration.status === status)
                          .length
                      : undefined,
                  }))}
                  onChange={(statuses) => {
                    setFilter((current) => ({ ...current, statuses }));
                    table.resetPage();
                  }}
                />
              </>
            }
            pagination={table.pagination}
          />
        </DashboardTableDialogBody>
        <DashboardTableDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {dashboardT('placements.close')}
          </Button>
        </DashboardTableDialogFooter>
      </DashboardTableDialogContent>
    </Dialog>
  );
}
